import { useState, useCallback } from 'react'
import { apiClient } from '@/lib/api-client'
import type { ArtJobResponse, FrontendArt } from '@/lib/api-types'
import { mapArtJobResponseToFrontend } from '@/lib/api-types'
import { useToast } from '@/hooks/use-toast'

export interface UseArtJobResult {
  job: ArtJobResponse | null
  loading: boolean
  error: string | null
  createArtJob: (
    prompt: string,
    inputPhotos: string[] | File[],
    seed?: number,
    width?: number,
    height?: number,
    mode?: string,
    imageUrl?: string,
    modoKV?: boolean,
    modoKVBranding?: boolean,
    modoLucky?: boolean,
    selectedVariations?: string[]
  ) => Promise<ArtJobResponse | null>
  fetchArtJob: (jobId: string) => Promise<ArtJobResponse | null>
  regenerateArtJob: (jobId: string) => Promise<ArtJobResponse | null>
  editArtJob: (jobId: string, editPrompt: string) => Promise<ArtJobResponse | null>
  rerollArtJob: (jobId: string, variationPrompt?: string) => Promise<ArtJobResponse | null>
  pollArtJob: (
    jobId: string,
    maxAttempts?: number,
    intervalMs?: number
  ) => Promise<ArtJobResponse | null>
}

export function useArtJob(): UseArtJobResult {
  const [job, setJob] = useState<ArtJobResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { toast } = useToast()

  // Convert File to data URL string (base64) - PRESERVING QUALITY
  const fileToBase64 = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        // Retorna a URL completa com prefixo data: para o backend processar
        // Sem compressão - mantém qualidade original
        const dataUrl = reader.result as string
        console.log(`[DEBUG] Foto enviada: ${(file.size / 1024 / 1024).toFixed(2)}MB em base64`)
        resolve(dataUrl)
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })
  }

  // Validar tamanho de payload
  const validatePayloadSize = (photoStrings: string[]): void => {
    let totalSize = 0
    photoStrings.forEach((photo, idx) => {
      const size = photo.length
      totalSize += size
      console.log(`[DEBUG] Foto ${idx + 1}: ${(size / 1024 / 1024).toFixed(2)} MB`)
    })
    
    const totalMB = totalSize / 1024 / 1024
    console.log(`[DEBUG] 📊 Tamanho total do payload: ${totalMB.toFixed(2)} MB`)
    
    // Limite de 50 MB (mesmo do backend)
    if (totalMB > 50) {
      throw new Error(
        `Payload muito grande: ${totalMB.toFixed(2)} MB (máximo 50 MB). ` +
        `Reduza a qualidade ou quantidade de imagens.`
      )
    }
  }

  const createArtJob = useCallback(
    async (
      prompt: string,
      inputPhotos: string[] | File[],
      seed?: number,
      width: number = 1024,
      height: number = 1024,
      mode: string = 'generate',
      imageUrl?: string,
      modoKV: boolean = false,
      modoKVBranding: boolean = false,
      modoLucky: boolean = false,
      selectedVariations?: string[]
    ): Promise<ArtJobResponse | null> => {
      try {
        setLoading(true)
        setError(null)
        
        console.log('[DEBUG] createArtJob - iniciando com prompt:', prompt)
        console.log('[DEBUG] createArtJob - fotos:', inputPhotos.length)
        console.log('[DEBUG] createArtJob - resolução:', `${width}x${height}`, 'mode:', mode)
        console.log('[DEBUG] createArtJob - MODO KV:', modoKV)
        console.log('[DEBUG] createArtJob - MODO KV BRANDING:', modoKVBranding)
        console.log('[DEBUG] createArtJob - MODO LUCKY:', modoLucky)
        console.log('[DEBUG] createArtJob - VARIAÇÕES SELECIONADAS:', selectedVariations)
        
        // Convert Files to base64 strings if needed
        let photoStrings: string[] = []
        if (inputPhotos.length > 0 && inputPhotos[0] instanceof File) {
          console.log('[DEBUG] Convertendo', inputPhotos.length, 'fotos para base64...')
          photoStrings = await Promise.all(
            (inputPhotos as File[]).map(file => fileToBase64(file))
          )
          console.log('[DEBUG] Fotos convertidas com sucesso')
        } else {
          photoStrings = inputPhotos as string[]
        }
        
        // Validar tamanho do payload (apenas se não for modo Lucky)
        if (!modoLucky) {
          validatePayloadSize(photoStrings)
        }
        
        const payload = {
          prompt,
          input_photos: modoLucky ? [] : photoStrings, // Modo Lucky não usa input_photos
          seed,
          width,
          height,
          mode: mode as 'generate' | 'regenerate' | undefined,
          image_url: imageUrl,
          model: 'gemini-2.5-flash-image-preview',
          modo_kv: modoKV,
          modo_kv_branding: modoKVBranding,
          modo_lucky: modoLucky,
          selected_variations: selectedVariations,
        }
        
        console.log('[DEBUG] 📤 Enviando payload ao backend')

        const response = await apiClient.createArtJob(payload)
        
        console.log('[DEBUG] ✅ createArtJob response:', response)
        console.log('[DEBUG] response.data:', response.data)
        
        setJob(response.data)
        
        toast({
          title: 'Job criado',
          description: 'Processando arte com IA...',
        })
        
        return response.data
      } catch (err: any) {
        console.error('[DEBUG] ❌ Erro ao criar art job:', err)
        console.error('[DEBUG] Erro completo:', JSON.stringify(err, null, 2))
        console.error('[DEBUG] Erro status:', err.response?.status)
        console.error('[DEBUG] Erro data:', err.response?.data)
        console.error('[DEBUG] Erro message:', err.message)
        
        const msg =
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.message ||
          'Erro ao criar job de arte'
        setError(msg)
        toast({
          title: 'Erro',
          description: msg,
          variant: 'destructive',
        })
        return null
      } finally {
        setLoading(false)
      }
    },
    [toast]
  )

  const fetchArtJob = useCallback(
    async (jobId: string): Promise<ArtJobResponse | null> => {
      try {
        const response = await apiClient.getArtJob(jobId)
        setJob(response.data)
        return response.data
      } catch (err: any) {
        const msg =
          err.response?.data?.detail ||
          err.message ||
          'Erro ao buscar job'
        setError(msg)
        console.error('[DEBUG] getArtJob error:', err)
        return null
      }
    },
    []
  )

  const regenerateArtJob = useCallback(
    async (jobId: string): Promise<ArtJobResponse | null> => {
      try {
        setLoading(true)
        setError(null)

        console.log('[DEBUG] regenerateArtJob - iniciando com jobId:', jobId)

        const response = await apiClient.regenerateArtJob(jobId)

        console.log('[DEBUG] ✅ regenerateArtJob response:', response)
        console.log('[DEBUG] response.data:', response.data)

        setJob(response.data)

        toast({
          title: 'Job regenerado',
          description: 'Processando arte com IA...',
        })

        return response.data
      } catch (err: any) {
        console.error('[DEBUG] ❌ Erro ao regenerar art job:', err)
        console.error('[DEBUG] Erro completo:', JSON.stringify(err, null, 2))
        console.error('[DEBUG] Erro status:', err.response?.status)
        console.error('[DEBUG] Erro data:', err.response?.data)
        console.error('[DEBUG] Erro message:', err.message)

        const msg =
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.message ||
          'Erro ao regenerar job de arte'
        setError(msg)
        toast({
          title: 'Erro',
          description: msg,
          variant: 'destructive',
        })
        return null
      } finally {
        setLoading(false)
      }
    },
    [toast]
  )

  const editArtJob = useCallback(
    async (jobId: string, editPrompt: string): Promise<ArtJobResponse | null> => {
      try {
        setLoading(true)
        setError(null)

        console.log('[DEBUG] editArtJob - iniciando com jobId:', jobId)
        console.log('[DEBUG] editArtJob - prompt de edição:', editPrompt)

        const response = await apiClient.editArtJob(jobId, editPrompt)

        console.log('[DEBUG] ✅ editArtJob response:', response)
        console.log('[DEBUG] response.data:', response.data)

        setJob(response.data)

        toast({
          title: 'Job editado',
          description: 'Processando arte com IA...',
        })

        return response.data
      } catch (err: any) {
        console.error('[DEBUG] ❌ Erro ao editar art job:', err)
        console.error('[DEBUG] Erro completo:', JSON.stringify(err, null, 2))
        console.error('[DEBUG] Erro status:', err.response?.status)
        console.error('[DEBUG] Erro data:', err.response?.data)
        console.error('[DEBUG] Erro message:', err.message)

        const msg =
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.message ||
          'Erro ao editar job de arte'
        setError(msg)
        toast({
          title: 'Erro',
          description: msg,
          variant: 'destructive',
        })
        return null
      } finally {
        setLoading(false)
      }
    },
    [toast]
  )

  const rerollArtJob = useCallback(
    async (jobId: string, variationPrompt?: string): Promise<ArtJobResponse | null> => {
      try {
        setLoading(true)
        setError(null)

        console.log('[DEBUG] rerollArtJob - iniciando com jobId:', jobId)
        console.log('[DEBUG] rerollArtJob - variation prompt:', variationPrompt || '(vazio - só variação)')

        const response = await apiClient.rerollArtJob(jobId, variationPrompt)

        console.log('[DEBUG] ✅ rerollArtJob response:', response)
        console.log('[DEBUG] response.data:', response.data)

        setJob(response.data)

        toast({
          title: 'Gerando variação',
          description: 'Processando nova variação da arte com IA...',
        })

        return response.data
      } catch (err: any) {
        console.error('[DEBUG] ❌ Erro ao gerar variação:', err)
        console.error('[DEBUG] Erro completo:', JSON.stringify(err, null, 2))
        console.error('[DEBUG] Erro status:', err.response?.status)
        console.error('[DEBUG] Erro data:', err.response?.data)
        console.error('[DEBUG] Erro message:', err.message)

        const msg =
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.message ||
          'Erro ao gerar variação de arte'
        setError(msg)
        toast({
          title: 'Erro',
          description: msg,
          variant: 'destructive',
        })
        return null
      } finally {
        setLoading(false)
      }
    },
    [toast]
  )

  const pollArtJob = useCallback(
    async (
      jobId: string,
      maxAttempts: number = 120,  // 🚀 Aumentado para 60 segundos (120 × 500ms)
      intervalMs: number = 500  // 500ms para resposta rápida
    ): Promise<ArtJobResponse | null> => {
      let attempts = 0

      return new Promise((resolve, reject) => {
        const interval = setInterval(async () => {
          attempts++
          
          // 🚀 Adaptar intervalo: mais rápido nos primeiros 3 segundos, depois normaliza
          let adaptiveInterval = intervalMs
          if (attempts > 6) {  // Após 3s de polling (6 × 500ms)
            adaptiveInterval = 1000  // Passa para 1s
          }
          
          try {
            const response = await fetchArtJob(jobId)
            
            if (!response) {
              clearInterval(interval)
              reject(new Error('Erro ao buscar status do job'))
              return
            }

            if (response.status === 'succeeded') {
              clearInterval(interval)
              const totalMs = attempts * intervalMs
              console.log(`[DEBUG] ✅ Arte concluída em ~${totalMs}ms (${attempts} tentativas)`)
              toast({
                title: 'Sucesso',
                description: 'Arte gerada com sucesso!',
              })
              resolve(response)
            } else if (response.status === 'failed') {
              clearInterval(interval)
              const errorMsg = response.error || 'Job falhou'
              toast({
                title: 'Erro',
                description: errorMsg,
                variant: 'destructive',
              })
              reject(new Error(errorMsg))
            } else if (attempts >= maxAttempts) {
              clearInterval(interval)
              const timeoutMsg = 'Timeout ao processar job'
              toast({
                title: 'Erro',
                description: timeoutMsg,
                variant: 'destructive',
              })
              reject(new Error(timeoutMsg))
            }
            // Caso contrário, continua polling (status: queued ou running)
          } catch (err) {
            clearInterval(interval)
            reject(err)
          }
        }, intervalMs)
      })
    },
    [fetchArtJob, toast]
  )

  return {
    job,
    loading,
    error,
    createArtJob,
    fetchArtJob,
    regenerateArtJob,
    editArtJob,
    rerollArtJob,
    pollArtJob,
  }
}


